import moment from "moment";
import { getLastUpdateDate, Ticket } from "../../Stores/Ticket";
import { decodeHtmlEntities } from "../../Helper/HtmlEntities";

// Compact relative age for switcher rows: "now" / "8 m" / "3 h" / "2 d" / "5 w".
export function formatCompactTime(ticket: Ticket): string {
  const then = getLastUpdateDate(ticket);
  const now = moment();
  const minutes = Math.max(0, now.diff(then, "minutes"));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes} m`;
  const hours = now.diff(then, "hours");
  if (hours < 24) return `${hours} h`;
  const days = now.diff(then, "days");
  if (days < 7) return `${days} d`;
  return `${now.diff(then, "weeks")} w`;
}

// Minimal one-line strip — the server/WS previews (data.text / .text) are already
// plain text; this only guards the content-string fallback. Intentionally lighter
// than ConversationItem's full tiptap render (a switcher row needs one clean line).
function stripPreview(input: string): string {
  return input
    .replace(/ ?\[\^\d+\|[^\]]*\]/g, "") // AI citation markers
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // markdown links -> text
    .replace(/[*_`#>]/g, "") // emphasis / code / heading / quote marks
    .replace(/\s+/g, " ") // collapse newlines/whitespace
    .trim();
}

// One-line preview of the latest message for a switcher row.
export function getConversationPreview(
  ticket: Ticket,
  translate: (key?: string) => string,
): string {
  const c = ticket.latestPublicComment;
  let text: string | undefined;
  if (c?.data?.text) text = c.data.text;
  else if (c?.text) text = c.text;
  else if (typeof c?.data?.content === "string") text = c.data.content;
  else if (ticket.title) text = ticket.title;

  if (!text || !text.trim()) {
    const t = translate("noMessages");
    return t === "noMessages" ? "No messages yet" : t;
  }
  // Stored text is HTML-escaped; the row renders it as a React text node.
  return decodeHtmlEntities(stripPreview(text));
}
